import { DatapointStatus, logUsage, PerformanceDatapoint } from 'owa-analytics';
import { ReplyWithAttachmentSuggestion, ReplyWithEndpointType } from 'owa-mail-store';
import { getDefaultAnchorMailboxCookie } from 'owa-ows-gateway/lib/anchormailbox';
import { lazyLogResponseReceivedV2 } from 'owa-search-instrumentation';
import { SubstrateSearchScenario } from 'owa-search-service';
import { getAccessTokenforResourceAsLazy } from 'owa-tokenprovider';
import { trace } from 'owa-trace';
import MRUAttachment from '../store/schema/MRUAttachment';
import convertMRUAttachmentToReplyWithAttachmentSuggestion from '../utils/convertMRUAttachmentToReplyWithAttachmentSuggestion';
import {
    shouldFetchMRUClassicAttachments,
    shouldLimitMRUCloudyAttachmentsToOne,
} from '../utils/getAttachmentTypesToFetch';

const CONTENT_TYPE = 'application/json;charset=utf-8';
const MRU_ENDPOINT = '/search/api/v1/recommendations';

// Used to retrieve shared classic attachments, along with cloudy attachments.
const MIXED_ATTACHMENTS_FLIGHT = 'recEnableSharedClassicAttachments';

// If enabled, at least two classic suggestions are guaranteed to be returned by the service.
// For testing purposes only.
const MAX_MRU_FILES1_FLIGHT = 'maxMRUFiles1';

export interface FetchMRUSuggestionsResult {
    suggestions: ReplyWithAttachmentSuggestion[] | null;
    traceId: string | null;
}

/*
 * onServiceCallStateChanged can be used to ensure that only one call is made to this service at a time.
 */
export default async function fetchMRUSuggestions(
    referenceItemId: string
): Promise<FetchMRUSuggestionsResult> {
    let suggestions: ReplyWithAttachmentSuggestion[] = null;
    let numResults: number = 0;
    let errorMessage: string = '';
    let queryResponseDatapoint: PerformanceDatapoint = null;
    let traceId: string | null = null;

    try {
        // Log that we are making a call to the MRU service.
        logUsage('MRUQueryExecuted');

        // There is custom data we want to add even if the service call fails,
        // so define the datapoint now.
        queryResponseDatapoint = new PerformanceDatapoint('QueryResponseInformation', {
            logVerbose: true,
        });

        // Determine whether we are fetching cloudy attachments or classic + cloudy attachments.
        let fetchClassicAttachments: boolean = shouldFetchMRUClassicAttachments();

        if (referenceItemId) {
            queryResponseDatapoint.addCustomData({
                endpoint: ReplyWithEndpointType.MRUContextual,
            });
        } else {
            // For this datapoint, just use MRUCloudy to mean "MRU" service, as opposed to Contextual service.
            queryResponseDatapoint.addCustomData({
                endpoint: ReplyWithEndpointType.MRUCloudy,
            });
        }

        // Formulate the request
        const token: string = await getTokenForMRUService();
        const requestInit: RequestInit = createRequestInit(
            token,
            fetchClassicAttachments,
            referenceItemId
        );

        // Log the client E2E latency
        queryResponseDatapoint.addCheckmark('Request');
        const timeSendingRequest = new Date().getTime();
        const response: Response = await fetch(MRU_ENDPOINT, requestInit);
        queryResponseDatapoint.addCheckmark('Response');
        const fetchTimeSpent = new Date().getTime() - timeSendingRequest;

        if (!response) {
            errorMessage = 'No response';
        } else {
            const responseBody = await response.text();
            let parsedResponse = null;
            try {
                parsedResponse = JSON.parse(responseBody);
            } catch (e2) {}

            traceId = parsedResponse?.Instrumentation?.TraceId;
            if (response.status == 200 /* OK */) {
                suggestions = getAttachmentsFromResponse(parsedResponse);
                numResults = suggestions.length;
            } else {
                errorMessage = response.status + '-' + response.statusText;
            }

            if (traceId) {
                lazyLogResponseReceivedV2.importAndExecute(
                    SubstrateSearchScenario.SuggestedAttachments,
                    traceId,
                    fetchTimeSpent,
                    response.status
                );
            }
        }
    } catch (e) {
        errorMessage = e.message;
    } finally {
        if (errorMessage) {
            trace.info(`getMRUSuggestions: Error ${errorMessage}`);
        }

        queryResponseDatapoint.addCustomData({ numResults: numResults });

        if (errorMessage) {
            queryResponseDatapoint.endWithError(
                DatapointStatus.ServerError,
                new Error(errorMessage)
            );
        } else {
            queryResponseDatapoint.end();
        }

        return { suggestions, traceId };
    }
}

async function getTokenForMRUService(): Promise<string> {
    let [token, tokenPromise] = getAccessTokenforResourceAsLazy(window.location.origin);

    // If token is not returned synchronously, we need to await on the tokenPromise
    if (!token) {
        token = await tokenPromise;
    }

    return token;
}

function createRequestInit(
    token: string,
    fetchClassicAttachments: boolean,
    referenceItemId: string
): RequestInit {
    const requestInit: RequestInit = {};
    requestInit.headers = new Headers();

    // Authorization
    (<Headers>requestInit.headers).set('Authorization', `Bearer ${token}`);

    // Content-Type
    (<Headers>requestInit.headers).set('Content-Type', CONTENT_TYPE);

    // Method
    requestInit.method = 'POST';

    // X-AnchorMailbox
    const defaultAnchorMailbox = getDefaultAnchorMailboxCookie();
    if (defaultAnchorMailbox) {
        (<Headers>requestInit.headers).append('X-AnchorMailbox', defaultAnchorMailbox);
    }

    // X-Client-LocalTime
    (<Headers>requestInit.headers).set('X-Client-LocalTime', new Date().toISOString());

    const xClientFlightsHeaders: string[] = [];

    if (fetchClassicAttachments) {
        xClientFlightsHeaders.push(MIXED_ATTACHMENTS_FLIGHT);

        if (shouldLimitMRUCloudyAttachmentsToOne()) {
            xClientFlightsHeaders.push(MAX_MRU_FILES1_FLIGHT);
        }
    }

    if (xClientFlightsHeaders.length) {
        (<Headers>requestInit.headers).set('X-Client-Flights', xClientFlightsHeaders.join(','));
    }

    const data = {
        EntityRequests: [
            referenceItemId
                ? {
                      Context: {
                          EntityId: referenceItemId,
                      },
                  }
                : {},
        ],
        Scenario: {
            Name: 'AttachFile',
        },
    };

    requestInit.body = JSON.stringify(data);

    return requestInit;
}

function getAttachmentsFromResponse(parsedResponse: any): ReplyWithAttachmentSuggestion[] {
    try {
        // MRU attachments
        const rawMRUAttachments: MRUAttachment[] =
            parsedResponse.EntitySets[0].ResultSets[0].Results;

        // Convert to ReplyWithAttachmentSuggestions.
        return rawMRUAttachments.map(mruAttachment =>
            convertMRUAttachmentToReplyWithAttachmentSuggestion(
                mruAttachment,
                parsedResponse?.Instrumentation?.TraceId
            )
        );
    } catch (e) {
        trace.info(`[fetchMRUSuggestions_getAttachmentsFromResponse] Error: ${e.message}`);
        return [];
    }
}
